import {
    Box,
    Hide,
    HStack,
    Image,
    Show,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react'
import { useContext, useEffect, useReducer, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import AxInfo from '../../components/Assessment/Instructions/Info'
import StepIndicator from '../../components/Assessment/Instructions/StepIndicator'
import Steps from '../../components/Assessment/Instructions/Steps'
import PageLoader from '../../components/PageLoader'
import { AuthContext } from '../../contexts/AuthContext'
import AxInstructionsContext from '../../contexts/AxInstructionsContext'
import PROCTORING_TYPE from '../../enums/proctoringType.js'
import useProctoring from '../../hooks/useProctoring/useProctoring.js'
import { initialState, reducer } from '../../reducers/axInstructionsReducer'
import { setSaveEventLogPreviewMode } from '../Attempt/_service.js'
import { getAxData } from './_services.js'
import Warning from './Warning.jsx'
import { useLocation } from 'react-router-dom'
import useFullScreen from '@/hooks/useFullScreen/useFullScreen.js'
import AX_INSTRUCTIONS_ACTION from '@/enums/axInstructionsActions.js'
import FULL_SCREEN_EXIT_STATES from '@/enums/fullScreenExitStates.js'
import { hasActiveViolation } from '@/utils/sharedAttemptStorage.js'
import dayjs from 'dayjs'

const Instructions = () => {
    const pathParams = useParams()
    const [isLoading, setIsLoading] = useState(true)
    const { setEntireScreenShareEnabled } = useProctoring()
    const { openFullScreen, isFullScreen, isFullScreenModeEnabled } =
        useFullScreen()

    const [assessment, setAssessment] = useState({})

    const { kalviumKc } = useContext(AuthContext)
    const { state: locationState, search } = useLocation()
    const isPreview = new URLSearchParams(search).get('isPreview') === 'true'
    const toast = useToast()
    const navigate = useNavigate()

    const [store, dispatch] = useReducer(reducer, {
        ...initialState,
        isPreview,
    })
    const [isPlagiarism, setIsPlagiarism] = useState(
        locationState?.isPlagiarism || false
    )
    const [isScreenExtended, setIsScreenExtended] = useState(
        locationState?.isScreenExtended || false
    )
    const AUTO_SUBMIT_TIMEOUT = 30 //30s

    const [autoSubmitTimer, setAutoSubmitTimer] = useState()
    const timerIntervalRef = useRef(null)

    useEffect(() => {
        setSaveEventLogPreviewMode(isPreview)
        return () => setSaveEventLogPreviewMode(false)
    }, [isPreview])

    useEffect(() => {
        localStorage.setItem('prevPage', window.location.pathname)
        if (kalviumKc.isLoggedIn()) {
            getAxData(
                pathParams.ax_slug,
                store.steps,
                dispatch,
                toast,
                navigate,
                setAssessment,
                setIsLoading,
                isPreview
            ).then()
        } else setIsLoading(false)
    }, [])

    useEffect(() => {
        if (assessment?.proctoring === PROCTORING_TYPE.REMOTE_PROCTORING) {
            setEntireScreenShareEnabled(true)
        }
    }, [assessment])

    useEffect(() => {
        const fullScreenExited = JSON.parse(localStorage.getItem('exited'))
        if (
            fullScreenExited &&
            fullScreenExited?.slug === pathParams?.ax_slug
        ) {
            setIsPlagiarism(true)
        }
    }, [])

    useEffect(() => {
        if (
            hasActiveViolation({
                locationState,
                slug: pathParams?.ax_slug,
            })
        ) {
            setIsPlagiarism(true)
        }
    }, [locationState?.isPlagiarism, pathParams?.ax_slug])

    useEffect(() => {
        if (isPlagiarism && assessment?.full_screen_integrity_mode === true) {
            const fullScreenExitInfo = localStorage.getItem('exited')
            const isActiveViolation = hasActiveViolation({
                locationState,
                slug: pathParams?.ax_slug,
            })

            if (!fullScreenExitInfo) {
                localStorage.setItem(
                    'exited',
                    JSON.stringify({
                        slug: pathParams?.ax_slug,
                        datetime: dayjs(),
                    })
                )
                setAutoSubmitTimer(AUTO_SUBMIT_TIMEOUT)
                dispatch({
                    type: AX_INSTRUCTIONS_ACTION.SET_FULL_SCREEN_EXIT_STATE,
                    value: isActiveViolation || isFullScreenModeEnabled
                        ? FULL_SCREEN_EXIT_STATES.WARNING_STEP
                        : FULL_SCREEN_EXIT_STATES.PROCTORING_SETUP_STEP,
                })
            } else {
                const fullScreenExitParsedData = JSON.parse(fullScreenExitInfo)
                const fullScreenExitedDateTime = dayjs(
                    fullScreenExitParsedData.datetime
                )
                const elapsedSeconds = dayjs().diff(
                    fullScreenExitedDateTime,
                    'seconds'
                )
                const remainingSeconds = Math.max(
                    AUTO_SUBMIT_TIMEOUT - elapsedSeconds,
                    0
                )
                setAutoSubmitTimer(remainingSeconds)
                dispatch({
                    type: AX_INSTRUCTIONS_ACTION.SET_FULL_SCREEN_EXIT_STATE,
                    value: isActiveViolation
                        ? FULL_SCREEN_EXIT_STATES.WARNING_STEP
                        : FULL_SCREEN_EXIT_STATES.PROCTORING_SETUP_STEP,
                })
            }
        }
    }, [isPlagiarism, assessment])

    useEffect(() => {
        if (
            [
                FULL_SCREEN_EXIT_STATES.WARNING_STEP,
                FULL_SCREEN_EXIT_STATES.PROCTORING_SETUP_STEP,
            ].includes(store?.fullScreenExitState)
        ) {
            timerIntervalRef.current = setInterval(() => {
                setAutoSubmitTimer(prevTimer => {
                    if (prevTimer <= 1) {
                        clearInterval(timerIntervalRef.current)
                        dispatch({
                            type: AX_INSTRUCTIONS_ACTION.SET_FULL_SCREEN_EXIT_STATE,
                            value: FULL_SCREEN_EXIT_STATES.AUTO_SUBMIT_ATTEMPT,
                        })
                        return 0
                    }
                    return prevTimer - 1
                })
            }, 1000)
        } else {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current)
                timerIntervalRef.current = null
            }
        }
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current)
            }
        }
    }, [store?.fullScreenExitState])

    const SwitchToPc = () => {
        return (
            <VStack h={'full'}>
                <Box textAlign={'center'} pt={4}>
                    <Image src="/images/logo_min.svg" alt="Logo" mx={'auto'} />
                </Box>
                <VStack justify={'center'} h={'max-content'} flex={1}>
                    <Image src="/images/switch_to_pc.svg" alt="Mobile phone" />
                    <Text
                        textAlign={'center'}
                        mx={'76px !important'}
                        fontSize={'sm'}
                        lineHeight={'21px'}
                    >
                        Please switch to a PC/Laptop to proceed with the
                        assessment
                    </Text>
                </VStack>
            </VStack>
        )
    }

    const autoSubmitWarningMessage = () => {
        return (
            <p className="text-center text-[#FFF7ED] text-xl">
                Resume your attempt in{' '}
                <span className="font-bold">{autoSubmitTimer} seconds</span> and
                prevent auto-submission.
            </p>
        )
    }

    return isLoading ? (
        <PageLoader />
    ) : (
        <>
            <Show above="md">
                <HStack height={'full'} gap={0}>
                    {/* left side of screen */}
                    <AxInfo assessment={assessment} />
                    <AxInstructionsContext.Provider value={{ store, dispatch }}>
                        {/* steps indicator in middle */}

                        <StepIndicator />
                        <Steps
                            assessment={assessment}
                            autoSubmitWarningMessage={autoSubmitWarningMessage}
                            isDisplayWarningMessage={
                                store?.fullScreenExitState ===
                                    FULL_SCREEN_EXIT_STATES.PROCTORING_SETUP_STEP &&
                                assessment?.full_screen_integrity_mode
                            }
                        />
                    </AxInstructionsContext.Provider>
                    <Warning
                        isOpen={
                            kalviumKc?.isLoggedIn() &&
                            isPlagiarism &&
                            (assessment?.full_screen_integrity_mode === true
                                ? store?.fullScreenExitState ===
                                  FULL_SCREEN_EXIT_STATES.WARNING_STEP
                                : true)
                        }
                        remLives={
                            store?.attempts?.find(
                                attempt =>
                                    !attempt.closed && attempt.full_screen_mode
                            )?.remaining_lives || 0
                        }
                        resumeTest={() => {
                            localStorage.removeItem('violation_detected')
                            if (assessment?.full_screen_integrity_mode) {
                                dispatch({
                                    type: AX_INSTRUCTIONS_ACTION.SET_FULL_SCREEN_EXIT_STATE,
                                    value: FULL_SCREEN_EXIT_STATES.PROCTORING_SETUP_STEP,
                                })
                            } else {
                                setIsPlagiarism(false)
                            }
                            openFullScreen()
                        }}
                        autoSubmitWarningMessage={autoSubmitWarningMessage}
                        isScreenExtended={isScreenExtended}
                        isFullScreenIntegrityMode={
                            assessment?.full_screen_integrity_mode
                        }
                    />
                </HStack>
            </Show>
            <Hide above="md">
                <SwitchToPc />
            </Hide>
        </>
    )
}

export default Instructions
